import { createSlice, PayloadAction } from '@reduxjs/toolkit';


export interface ColorPalette {
  id: string;
  name: string;
  colors: string[];
  isCustom?: boolean;
}

export interface TypographyVariant {
  fontFamily: string;
  fontSize: number;
  isBold: boolean;
  isItalic: boolean;
  isUppercase: boolean;
  lineHeight?: number;
  letterSpacing?: number;
}

export interface TypographyState {
  title: TypographyVariant;
  subtitle: TypographyVariant;
  heading: TypographyVariant;
  subheading: TypographyVariant;
  smallHeading: TypographyVariant; // Corresponds to "Geologica" / Section Header
  body: TypographyVariant;
  quote: TypographyVariant;
  caption: TypographyVariant;
}

export interface BrandKitState {
  id?: string;
  name: string;
  description: string;
  category: string;
  logos: {
    main: string | null;
    light: string | null;
    secondary: string | null;
    dark: string | null;
  };
  colors: ColorPalette[];
  typography: TypographyState;
}

const defaultTypography: TypographyState = {
  title: { fontFamily: 'DM Sans', fontSize: 80, isBold: true, isItalic: false, isUppercase: false },
  subtitle: { fontFamily: 'DM Sans', fontSize: 72, isBold: true, isItalic: false, isUppercase: false },
  heading: { fontFamily: 'Geist Mono', fontSize: 56, isBold: true, isItalic: false, isUppercase: false },
  subheading: { fontFamily: 'Geist Mono', fontSize: 48, isBold: true, isItalic: false, isUppercase: false },
  smallHeading: { fontFamily: 'Geologica', fontSize: 40, isBold: true, isItalic: false, isUppercase: false },
  body: { fontFamily: 'Geologica', fontSize: 28, isBold: true, isItalic: false, isUppercase: false },
  quote: { fontFamily: 'Bricolage Grotesque', fontSize: 32, isBold: true, isItalic: true, isUppercase: false },
  caption: { fontFamily: 'Inconsolata', fontSize: 32, isBold: true, isItalic: false, isUppercase: false },
};

const initialState: BrandKitState = {
  name: '',
  description: '',
  category: '',
  logos: {
    main: null,
    light: null,
    secondary: null,
    dark: null,
  },
  colors: [],
  typography: defaultTypography,
};

const brandKitSlice = createSlice({
  name: 'brandKit',
  initialState,
  reducers: {
    resetBrandKit: () => initialState,
    updateBrandKit: (state, action: PayloadAction<Partial<BrandKitState>>) => {
      return { ...state, ...action.payload };
    },
    setBrandKit: (_, action: PayloadAction<BrandKitState>) => {
      return action.payload;
    },
    setLogo: (state, action: PayloadAction<{ type: keyof BrandKitState['logos']; url: string | null }>) => {
      state.logos[action.payload.type] = action.payload.url;
    },
    setColors: (state, action: PayloadAction<ColorPalette[]>) => {
      state.colors = action.payload;
    },
    addColorPalettes: (state, action: PayloadAction<ColorPalette[]>) => {
      state.colors = [...state.colors, ...action.payload];
    },
    prependColorPalette: (state, action: PayloadAction<ColorPalette>) => {
      state.colors = [action.payload, ...state.colors];
    },
    removeColorPalette: (state, action: PayloadAction<string>) => {
      state.colors = state.colors.filter(c => c.id !== action.payload);
    },
    updateColorPalette: (state, action: PayloadAction<{ id: string; changes: Partial<ColorPalette> }>) => {
      const index = state.colors.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.colors[index] = { ...state.colors[index], ...action.payload.changes };
      }
    },
    updateTypography: (state, action: PayloadAction<{ type: keyof TypographyState; changes: Partial<TypographyVariant> }>) => {
      state.typography[action.payload.type] = { ...state.typography[action.payload.type], ...action.payload.changes };
    }
  },
});

export const {
  resetBrandKit,
  updateBrandKit,
  setLogo,
  setColors,
  addColorPalettes,
  prependColorPalette,
  removeColorPalette,
  updateColorPalette,
  updateTypography,
  setBrandKit
} = brandKitSlice.actions;
export default brandKitSlice.reducer;

